import React, { useState } from 'react';
import logoIcon from '../assets/logo-icon-exact.png';
import { supabase } from '../supabaseClient';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Footer from '../components/Footer';

const STRIPE_MONTHLY = 'https://buy.stripe.com/4gMaEW7lb0sOcPDa7q5ZC04';
const STRIPE_ANNUAL = 'https://buy.stripe.com/3cIaEWgVL1wSg1P3J25ZC03';

function Subscribe({ darkMode, toggleDark }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [promoCode, setPromoCode]     = useState(searchParams.get('code') || '');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [promoError, setPromoError]   = useState('');

  const normalizedPromoCode = promoCode.trim().toUpperCase();
  const hasPromoCode = normalizedPromoCode.length > 0;
  const isTradeProPromo = normalizedPromoCode === 'TRADEPRO';
  const checkoutUrl = selectedPlan === 'annual' ? STRIPE_ANNUAL : STRIPE_MONTHLY;

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setPromoError('');
    if (!selectedPlan && !isTradeProPromo) {
      setError('Please choose a monthly or annual plan before you create your account.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match. Please confirm your password.');
      return;
    }
    setLoading(true);

    const { data, error: signUpError } = await supabase.auth.signUp({ email, password });

    if (signUpError) {
      setError(signUpError.message);
      setLoading(false);
      return;
    }

    // Supabase quirk: duplicate email returns a fake user with identities: []
    // instead of an error. Catch this explicitly so we don't create a ghost session.
    if (data.user?.identities?.length === 0) {
      setError('This email already has an account. Please sign in instead.');
      setLoading(false);
      return;
    }

    // Get the user ID from whichever is available:
    //   data.session.user.id — email confirmation OFF (immediate session)
    //   data.user.id         — email confirmation ON (user created, unconfirmed)
    const userId = data.session?.user?.id || data.user?.id;

    if (!userId) {
      setError('Account creation failed. Please try again.');
      setLoading(false);
      return;
    }

    // Explicitly hydrate the new session on the client before any authenticated
    // DB call. signUp() normally does this automatically, but the client can be
    // in an in-between state if onAuthStateChange fires and clears the token
    // before our INSERT runs.
    if (data.session) {
      await supabase.auth.setSession({
        access_token:  data.session.access_token,
        refresh_token: data.session.refresh_token,
      });
    }

    const code = normalizedPromoCode;
    if (code) {
      // Promo path: redeemed after auth settles in App.js.
      sessionStorage.setItem('trp_pending_promo', code);
      window.location.replace('/quiz');
      return;
    }

    // Continue directly to checkout after account creation.
    const planCheckoutUrl = `${checkoutUrl}?client_reference_id=${encodeURIComponent(userId)}`;
    window.location.replace(planCheckoutUrl);
  };

  return (
    <div className="auth-page">
      <header>
        <div className="logo" onClick={() => navigate('/')}>
          <div className="logo-icon"><img src={logoIcon} alt="" /></div>
          <div className="logo-text">TradeReady<span>Pro</span></div>
        </div>
        <div className="header-right">
          <Link to="/login" className="dark-toggle">Login</Link>
          <Link to="/quiz" className="dark-toggle">Home</Link>
          <Link to="/settings" className="dark-toggle" title="Settings">⚙️</Link>
        </div>
      </header>

      <div className="auth-wrap">
        <div className="auth-card">
          <h2>Create Your Account</h2>
          <p className="auth-sub">
            Create your account and continue to secure checkout.
          </p>

          <div className="signup-flow-header">
            <div className="signup-flow-step">Step 1: Choose your plan</div>
            <div className="signup-plan-picker" style={{ marginBottom: '1rem' }}>
              <button
                type="button"
                className={`signup-plan ${selectedPlan === 'monthly' ? 'selected' : ''}`}
                aria-pressed={selectedPlan === 'monthly'}
                onClick={() => setSelectedPlan('monthly')}
              >
                <div className="signup-plan-name">Monthly</div>
                <div className="signup-plan-price"><span className="price-was">$24.99</span> $9.99<span>/mo</span></div>
              </button>
              <button
                type="button"
                className={`signup-plan ${selectedPlan === 'annual' ? 'selected' : ''}`}
                aria-pressed={selectedPlan === 'annual'}
                onClick={() => setSelectedPlan('annual')}
              >
                <div className="signup-plan-name">Annual <span className="plan-badge">SAVE 34%</span></div>
                <div className="signup-plan-price">$199.00<span>/yr</span></div>
              </button>
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textAlign: 'center', marginBottom: '0.25rem' }}>
              Secure Stripe checkout. Payment is required before full access begins.
            </div>
          </div>

          {error && <div className="auth-error">{error}</div>}
          {promoError && <div className="auth-error">{promoError} Redirecting to your dashboard…</div>}

          <form onSubmit={handleSignup}>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email" value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com" required autoFocus
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password" value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Min 6 characters" minLength={6} required
              />
            </div>
            <div className="form-group">
              <label>Confirm Password</label>
              <input
                type="password" value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password" minLength={6} required
              />
            </div>
            <div className="form-group">
              <label>
                Promo Code{' '}
                <span style={{ fontWeight: 400, opacity: 0.6 }}>(optional)</span>
              </label>
              <input
                type="text" value={promoCode}
                onChange={e => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Enter promo code"
                style={{ textTransform: 'uppercase' }}
              />
              <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '0.35rem' }}>
                {isTradeProPromo
                  ? 'TradePro code unlocks the plan-free signup flow.'
                  : 'Promo codes grant a 60-day trial when valid.'}
              </div>
            </div>
            <button type="submit" className="btn-auth" disabled={loading || (!selectedPlan && !isTradeProPromo)}>
              {loading ? (hasPromoCode ? 'Applying promo…' : 'Creating account…') : (hasPromoCode ? 'Apply Promo & Continue →' : selectedPlan ? `Create ${selectedPlan === 'annual' ? 'Annual' : 'Monthly'} Account →` : 'Choose a plan to continue')}
            </button>
          </form>

          <div className="auth-footer">
            Already have an account? <Link to="/login">Sign In</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Subscribe;
